import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { toast } from 'sonner';
import { 
  Download, 
  RefreshCw, 
  Loader2, 
  AlertTriangle,
  CheckCircle2,
  FolderOpen,
  FileText
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

export default function RestoreTab() {
  const [loading, setLoading] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResults, setRestoreResults] = useState(null);

  const fetchBackupInfo = async () => {
    try {
      setLoadingInfo(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/storage/backup-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupInfo(response.data);
    } catch (error) {
      toast.error('Failed to fetch backup info: ' + (error.response?.data?.detail || error.message));
      setBackupInfo(null);
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleRestore = async () => {
    if (!window.confirm('⚠️ WARNING: This will restore ALL data from R2 backup.\n\nExisting records with the same ID will be overwritten.\n\nAre you sure you want to continue?')) {
      return;
    }

    try {
      setRestoring(true);
      setRestoreResults(null);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/storage/restore-from-r2`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000 // 5 minute timeout for large restores
      });
      setRestoreResults(response.data);
      toast.success('Restore completed successfully!');
      // Refresh backup info
      await fetchBackupInfo();
    } catch (error) {
      toast.error('Restore failed: ' + (error.response?.data?.detail || error.message));
      setRestoreResults({ error: error.response?.data?.detail || error.message });
    } finally {
      setRestoring(false);
    }
  };

  const totalFiles = backupInfo ? Object.values(backupInfo.collections || {}).reduce((sum, c) => sum + (c.count || 0), 0) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-xl">
              <Download className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Restore from R2 Backup</h2>
              <p className="text-sm text-slate-500">Restore data from encrypted R2 backup files</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchBackupInfo}
            disabled={loadingInfo}
            className="gap-2"
          >
            {loadingInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Scan Backups
          </Button>
        </div>
      </Card>

      {/* Backup Info */}
      {backupInfo && (
        <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <FolderOpen className="w-4 h-4" />
            Available Backups in R2
          </h3>
          
          {backupInfo.error ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="text-red-700">{backupInfo.error}</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-slate-800">{totalFiles}</p>
                  <p className="text-xs text-slate-500">Total Files</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-blue-600">{backupInfo.collections?.expenses?.count || 0}</p>
                  <p className="text-xs text-blue-600">Expenses</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-emerald-600">{backupInfo.collections?.silica_loads?.count || 0}</p>
                  <p className="text-xs text-emerald-600">Silica Loads</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-purple-600">{backupInfo.collections?.employees?.count || 0}</p>
                  <p className="text-xs text-purple-600">Employees</p>
                </div>
              </div>

              {/* Collection Details */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Collection</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Files</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Total Size</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Latest Backup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(backupInfo.collections || {}).map(([name, info]) => (
                      <tr key={name} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-slate-700 capitalize">{name.replace(/_/g, ' ')}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-mono text-slate-600">{info.count || 0}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-slate-600">{formatBytes(info.total_size || 0)}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-slate-500 text-sm">{info.latest || '-'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Restore Section */}
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-amber-900 text-sm mb-1">Restore All Data from R2</h3>
            <p className="text-sm text-amber-700 mb-3">
              This will restore all records from the encrypted backup files in R2.
              <strong> Existing records with matching IDs will be overwritten.</strong>
            </p>
            <Button
              onClick={handleRestore}
              disabled={restoring || !backupInfo || totalFiles === 0}
              className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
            >
              {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {restoring ? 'Restoring...' : 'Restore All Data'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Restore Results */}
      {restoreResults && (
        <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            {restoreResults.error ? (
              <AlertTriangle className="w-4 h-4 text-red-600" />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            )}
            Restore Results
          </h3>
          
          {restoreResults.error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-700">{restoreResults.error}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                <p className="text-emerald-700 font-medium">
                  Successfully restored {restoreResults.total_restored || 0} records
                </p>
              </div>
              
              {restoreResults.results && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Collection</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Restored</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(restoreResults.results).map(([name, info]) => (
                        <tr key={name} className="border-b border-slate-100">
                          <td className="py-2 px-3 capitalize">{name.replace(/_/g, ' ')}</td>
                          <td className="py-2 px-3 text-right text-emerald-600 font-medium">{info.restored || 0}</td>
                          <td className="py-2 px-3 text-right text-red-600">{info.errors || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Instructions */}
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">How R2 Backup Works</h3>
        <div className="space-y-2 text-sm text-slate-600">
          <p>• <strong>Automatic Backup:</strong> Each record is saved as an encrypted CSV file when created/updated</p>
          <p>• <strong>File Location:</strong> R2 → backup/{'{collection}'}/{'{date}-{seq}-{category}'}.csv.enc</p>
          <p>• <strong>Encryption:</strong> Files are encrypted using Fernet (AES-128)</p>
          <p>• <strong>Restore:</strong> Downloads, decrypts, and upserts records into the database</p>
        </div>
      </Card>
    </div>
  );
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
